import { GraphQLClient } from "graphql-request";
import secureLocalStorage from "react-secure-storage";

const STORAGE_KEY = "wordpress-user";

/**
 * Custom event broadcast when the auth session can no longer be refreshed.
 * useAuth listens for this and triggers a logout/redirect.
 */
export const AUTH_EXPIRED_EVENT = "auth-expired";

/**
 * Retrieves the authentication token from secure storage.
 * @returns The authentication token string, or empty string if not found
 */
export function getAuthToken(): string {
  try {
    const jsonUser = secureLocalStorage.getItem(STORAGE_KEY) as string;
    if (jsonUser) {
      const userData = JSON.parse(jsonUser);
      return userData.user?.token || "";
    }
  } catch (err) {
    console.error("Failed to parse stored auth data", err);
  }
  return "";
}

function setStoredToken(newToken: string): void {
  try {
    const jsonUser = secureLocalStorage.getItem(STORAGE_KEY) as string;
    if (!jsonUser) return;
    const userData = JSON.parse(jsonUser);
    if (!userData?.user) return;
    userData.user.token = newToken;
    secureLocalStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
  } catch (err) {
    console.error("Failed to persist refreshed token", err);
  }
}

let refreshInFlight: Promise<string | null> | null = null;
let lastRefreshAt = 0;
let lastRefreshedToken: string | null = null;
const REFRESH_GRACE_MS = 5000;

/**
 * Calls /refresh to mint a new short-lived access token. Coalesces
 * concurrent calls — the inflight singleton handles bursts that arrive
 * before the first /refresh returns; the grace window handles stragglers
 * that arrive shortly *after* (when the rotated-out cookie would 401).
 */
export async function refreshAccessToken(): Promise<string | null> {
  // Grace window: a refresh just succeeded — reuse that token instead of
  // hitting /refresh again with a rotated-out cookie that's about to 401.
  if (lastRefreshedToken && Date.now() - lastRefreshAt < REFRESH_GRACE_MS) {
    return lastRefreshedToken;
  }

  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + "/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return null;
      const json = await res.json();
      const token = json?.data?.token;
      if (!token) return null;
      setStoredToken(token);
      lastRefreshAt = Date.now();
      lastRefreshedToken = token as string;
      return token as string;
    } catch (err) {
      console.error("refresh failed", err);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function logoutServer(): Promise<void> {
  try {
    await fetch(process.env.REACT_APP_API_URL + "/logout", {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* swallow */
  }
}

/**
 * fetch wrapper that auto-refreshes the access token on 401 and retries
 * the original request once. If refresh fails, dispatches the
 * AUTH_EXPIRED_EVENT and returns the 401 response.
 */
/**
 * Always overlay the LATEST access token from storage onto the request's
 * auth header. GraphQLClient instances cache their headers at construction
 * time, so a long-lived client would keep sending an old token after a
 * refresh. This sidesteps that by reading storage on every fetch.
 *
 * If storage is empty (somehow cleared between requests), we proactively
 * call /refresh before firing, instead of letting the request fail with no
 * auth and triggering the AUTH_EXPIRED cascade.
 */
async function applyLatestAuth(init?: RequestInit): Promise<RequestInit> {
  let token = getAuthToken();
  if (!token) {
    // Storage was cleared but the refresh cookie may still be valid — get
    // a fresh access token before firing.
    const refreshed = await refreshAccessToken();
    if (refreshed) token = refreshed;
  }
  if (!token) return init ?? {};
  const headers = new Headers(init?.headers ?? {});
  const scheme = token.includes(".") ? "JWT" : "Invite";
  headers.set("authorization", `${scheme} ${token}`);
  return { ...init, headers };
}

export async function fetchWithRefresh(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const initWithAuth = await applyLatestAuth(init);
  const first = await fetch(input, initWithAuth);
  if (first.status !== 401) return first;

  const newToken = await refreshAccessToken();
  if (!newToken) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    return first;
  }

  const headers = new Headers(initWithAuth.headers ?? {});
  const scheme = newToken.includes(".") ? "JWT" : "Invite";
  headers.set("authorization", `${scheme} ${newToken}`);
  return fetch(input, { ...initWithAuth, headers });
}

/**
 * Creates and configures a GraphQL client with authentication headers.
 * Uses a custom fetch that auto-refreshes on 401.
 */
export function getClient(path: string = "/graphql"): GraphQLClient {
  const token = getAuthToken();
  const authScheme = token.includes(".") ? "JWT" : "Invite";
  return new GraphQLClient(process.env.REACT_APP_API_URL + path, {
    headers: {
      authorization: token ? `${authScheme} ${token}` : "",
    },
    credentials: "include",
    fetch: fetchWithRefresh,
  });
}
